<template>
  <div
    data-component="HeaderComponent"
    class="header-component"
  >
    <nav
      class="header"
      :aria-label="ARIA_LABELS.MAIN_NAV"
    >
      <div
        v-if="commonStore.showLogo"
        id="header-logo"
      >
        <div>
          <a
            name="header_logo"
            :href="headerClickable ? EXTERNAL_WEBS.ASISA_WEB_GLOBAL : undefined"
            :class="{ 'no-clickable': !!commonStore.infoMediator }"
            @click="goToAsisa"
            :aria-label="logoAltText"
          >
            <figure class="figure-size">
              <img
                v-if="commonStore.infoMediator"
                class="mediator-logo"
                :src="MEDIATOR_IMAGE_ROOT + commonStore.infoMediator.mediatorImage"
                :alt="logoAltText"
              />
              <div
                v-else-if="businessLineName === 'life'"
                class="life"
              ></div>
              <i
                v-else
                :class="businessStore.logo"
              ></i>
              <figcaption>{{ logoAltText }}</figcaption>
            </figure>
          </a>
        </div>
      </div>

      <BaseButton
        v-if="businessLine && !commonStore.infoMediator"
        class="help-button"
        :icon="!isMobile ? 'icon-message-text-01' : undefined"
        :name="'support-modal-btn'"
        :id="'support-modal-btn'"
        @click="openSupportModal"
      >
        {{ t('header.help-text') }}
      </BaseButton>
    </nav>
  </div>
</template>

<script setup lang="ts">
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import { useIsMobile } from '@/modules/core/composables/useIsMobile'
  import { ARIA_LABELS } from '@/modules/core/constants/ariaLabels'
  import EXTERNAL_WEBS from '@/modules/core/constants/externalPages'
  import { computed } from 'vue'

  import GTMEventType from '@/modules/core/models/GTMEventType'
  import { useBusinessStore } from '@/modules/core/stores/businessStore'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useGTMStore } from '@/modules/core/stores/gtmStore'
  import { useSharedGtmDataStore } from '@/modules/core/stores/sharedGtmDataStore'
  import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'
  import { useI18n } from 'vue-i18n'
  import BranchEnum from '../../models/BranchEnum'

  const MEDIATOR_IMAGE_ROOT = import.meta.env.VITE_API_URL_DXP_CONTENT + '/mediadores/logos/'

  const { t } = useI18n()
  const businessStore = useBusinessStore()
  const supportModalStore = useSupportModalStore()
  const commonStore = useCommonStore()
  const businessLine = computed(() => businessStore.getBusinessLine())
  const businessLineName = computed(() => businessStore.getBusinessLineName())
  const headerClickable = computed(
    () =>
      !commonStore.infoMediator &&
      businessLineName.value.toLowerCase() !== BranchEnum.HOME.toLowerCase()
  )
  const logoAltText = computed(() => (commonStore.infoMediator ? 'Logo' : t('header.logo')))
  const gmtStore = useGTMStore()
  const sharedGtmDataStore = useSharedGtmDataStore()
  const { isMobile } = useIsMobile()

  const goToAsisa = (event: Event) => {
    if (!headerClickable.value) {
      event.preventDefault()
      return
    }
    event.preventDefault()
    gmtStore.sendEvent({
      event: GTMEventType.LOGO,
      click_name: 'header_logo',
      click_id: 'header-logo',
      btn_label: t('header.logo')
    })
    setTimeout(() => {
      // give time to gmt to send the event
      const isPymes = !!(
        commonStore.pymesBusinessLines && Object.keys(commonStore.pymesBusinessLines).length > 0
      )
      const url = isPymes
        ? import.meta.env.VITE_API_URL_DXP_ROOT + import.meta.env.VITE_PYMES_URL_PATH_HOME
        : import.meta.env.VITE_API_URL_DXP_ROOT
      window.location.href = url
    }, 100)
  }

  function openSupportModal() {
    gmtStore.sendEvent({
      event: GTMEventType.CALLMEBACK_BTN,
      click_name: 'support-modal-btn',
      click_id: 'support-modal-btn',
      btn_label: t('header.help-text'),
      value: sharedGtmDataStore.policyValue,
      num_solicitud: sharedGtmDataStore.hiringNumber,
      producto: businessStore.getInsurance()?.productName ?? undefined,
      item_id: businessStore.getInsurance()?.productCode ?? undefined,
      num_asegurados: sharedGtmDataStore.forWhomPeople ?? sharedGtmDataStore.forWhomPets,
      cobert_adicionales: sharedGtmDataStore.additionalCoverages ?? false,
      otra_comp: sharedGtmDataStore.anotherCompany,
      capital: sharedGtmDataStore.capital,
      tipo_beneficiario: sharedGtmDataStore.beneficiaries_type,
      num_beneficiarios: sharedGtmDataStore.beneficiaries_number,
      num_mascotas: sharedGtmDataStore.forWhomPets,
      tipo_viaje: sharedGtmDataStore.travel_type,
      destino_viaje: sharedGtmDataStore.travel_destination,
      duracion_viaje: sharedGtmDataStore.travel_duration,
      ramo: sharedGtmDataStore.ramo
    })
    supportModalStore.setOpenModal(true)
  }
</script>

<style lang="scss" scoped>
  .header-component {
    .header {
      min-height: $size-80;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: $spacing-l;

      .figure-size {
        display: grid;

        .life {
          width: 9.375rem;
          height: 1.625rem;
          display: block;
          background: url('/src/assets/img/logos/blue/asisa_vida.svg') no-repeat;
          background-size: contain;
        }

        .mediator-logo {
          width: 9.375rem;
          height: auto;
          display: block;
          background-size: contain;
        }
      }

      #header-logo {
        a {
          display: block;
          text-decoration: none;

          &.no-clickable {
            cursor: default;
            pointer-events: none;
          }
        }

        i {
          font-size: var(--font-size-lg);
          line-height: var(--line-height-44);
        }

        figcaption {
          display: none;
        }

        &:has(.BackButton) {
          .figure-size {
            display: none;
          }
        }
      }

      .text-overlay {
        position: absolute;
        bottom: $size-10;
        right: $size-10;
        color: rgb(128 15 15 / 50%);
        padding: $padding-5;
        border-radius: $b-radius-3;
      }

      .help-button {
        padding: $spacing-l;
        font-size: $font-size-m;
        background-color: $color-primary-light;
        margin-right: 0;
        width: unset;

        &:hover {
          background: $color-brand-400;
        }

        &:active {
          background-color: $color-brand-400;
          background-size: 100%;
          transition: background 0s;
        }
      }
    }

    @include breakpoint-lg {
      .header {
        .figure-size {
          .life {
            width: 14.625rem;
            height: 2.25rem;
          }
        }
      }
    }

    @include breakpoint-xl {
      .header {
        height: $size-90;
        padding: 1rem 0;
      }
    }
  }
</style>
